// frontend/src/utils/publicationStatus.js
// NextScenes publication metadata helper
// Build: 2026-06-17-published-book-display-v4
//
// Single rule: publication state comes from the story record, not from title guessing.
// Known title assets are used only as display fallbacks for records already marked published/completed.

function safeText(value) {
  return String(value || "").trim();
}

function normalizePublicationTitle(value) {
  return safeText(value)
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9àâçéèêëîïôûùüÿñæœ\s-]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const PUBLICATION_STATUSES = Object.freeze({
  IN_DEVELOPMENT: "inDevelopment",
  COMPLETED: "completed",
  PUBLISHED_AMAZON: "publishedAmazon",
  PUBLISHED_EXTERNAL: "publishedExternal",
});

export function normalizePublicationStatus(value) {
  const raw = safeText(value);
  const lower = raw.toLowerCase();

  if (!raw) return PUBLICATION_STATUSES.IN_DEVELOPMENT;

  if (
    lower === "publishedamazon" ||
    lower === "published_amazon" ||
    lower === "amazon" ||
    lower === "published"
  ) {
    return PUBLICATION_STATUSES.PUBLISHED_AMAZON;
  }

  if (
    lower === "publishedexternal" ||
    lower === "published_external" ||
    lower === "external"
  ) {
    return PUBLICATION_STATUSES.PUBLISHED_EXTERNAL;
  }

  if (lower === "completed" || lower === "complete" || lower === "polished") {
    return PUBLICATION_STATUSES.COMPLETED;
  }

  return PUBLICATION_STATUSES.IN_DEVELOPMENT;
}

export function isPublishedStatus(status) {
  const normalized = normalizePublicationStatus(status);
  return (
    normalized === PUBLICATION_STATUSES.PUBLISHED_AMAZON ||
    normalized === PUBLICATION_STATUSES.PUBLISHED_EXTERNAL
  );
}

export function isKnownPublishedBook(story) {
  return Boolean(knownPublishedAssetForStory(story));
}

export function isPublishedBook(story) {
  return isPublishedStatus(
    story?.publicationStatus || story?.publishingStatus
  );
}

function knownPublishedAssetForStory(story) {
  const title = normalizePublicationTitle(story?.publishedTitle || story?.publicationTitle || story?.title);

  if (title.includes("reflections of the wayfarer")) {
    return {
      coverUrl: "/images/covers/reflections-of-the-wayfarer-en.webp",
      publishedAuthorName: "Goddy Oguzie",
      publishedTitle: "Reflections of the Wayfarer",
      amazonSearchQuery: "Reflections of the Wayfarer Goddy Oguzie",
    };
  }

  if (
    title.includes("réflexions dun voyageur") ||
    title.includes("reflexions dun voyageur") ||
    title.includes("réflexions d un voyageur") ||
    title.includes("reflexions d un voyageur")
  ) {
    return {
      coverUrl: "/images/covers/reflexions-du-voyageur-fr.webp",
      publishedAuthorName: "Goddy Oguzie",
      publishedTitle: "Réflexions d’un Voyageur",
      amazonSearchQuery: "Réflexions d’un Voyageur Goddy Oguzie",
    };
  }

  if (title.includes("ugo and her pet dog")) {
    return {
      coverUrl: "/images/covers/featured-flat/ugo-her-pet-dog.webp",
      publishedAuthorName: "Goddy Oguzie",
      publishedTitle: "Ugo and Her Pet Dog",
      amazonSearchQuery: "Ugo and Her Pet Dog Goddy Oguzie",
    };
  }

  if (title.includes("ugo and the butterfly woman")) {
    return {
      coverUrl: "/images/covers/ugo-butterfly-woman.webp",
      publishedAuthorName: "Goddy Oguzie",
      publishedTitle: "Ugo and the Butterfly Woman",
      amazonSearchQuery: "Ugo and the Butterfly Woman Goddy Oguzie",
    };
  }

  if (title.includes("ugo et la femme papillon") || title.includes("ugo et la femme-papillon")) {
    return {
      coverUrl: "/images/covers/ugo-femme-papillon.webp",
      publishedAuthorName: "Goddy Oguzie",
      publishedTitle: "Ugo et la Femme-Papillon",
      amazonSearchQuery: "Ugo et la Femme-Papillon Goddy Oguzie",
    };
  }

  return null;
}

export function getPublicationUrl(story) {
  const explicit = safeText(
    story?.publicationUrl ||
      story?.amazonUrl ||
      story?.amazonPageUrl ||
      story?.publishedUrl ||
      story?.externalPublicationUrl
  );

  if (explicit) return explicit;

  const asset = isPublishedBook(story) ? knownPublishedAssetForStory(story) : null;
  return asset?.amazonSearchQuery
    ? `https://www.amazon.com/s?k=${encodeURIComponent(asset.amazonSearchQuery)}`
    : "";
}

export function getPublicationCoverUrl(story) {
  const explicitPublicationCover = safeText(
    story?.publicationCoverUrl ||
      story?.publishedCoverUrl ||
      story?.finalCoverImageUrl
  );

  if (explicitPublicationCover) return explicitPublicationCover;

  const asset = isPublishedBook(story)
    ? knownPublishedAssetForStory(story)
    : null;

  const assetCover = safeText(asset?.coverUrl);
  if (assetCover) return assetCover;

  return safeText(
    story?.coverImageUrl ||
      story?.coverUrl ||
      story?.coverImage ||
      story?.imageUrl
  );
}

export function getPublishedAuthorName(story, fallback = "") {
  const asset = isPublishedBook(story)
    ? knownPublishedAssetForStory(story)
    : null;

  return (
    safeText(
      story?.publishedAuthorName ||
        story?.publicationAuthorName ||
        asset?.publishedAuthorName ||
        story?.displayAuthorName ||
        story?.authorName
    ) ||
    safeText(fallback) ||
    safeText(story?.authorProfile?.name) ||
    safeText(story?.authorUsername) ||
    safeText(story?.createdBy) ||
    safeText(story?.author)
  );
}

export function getPublishedTitle(story) {
  const asset = isPublishedBook(story) ? knownPublishedAssetForStory(story) : null;
  return safeText(story?.publishedTitle || story?.publicationTitle || asset?.publishedTitle || story?.title);
}

export function getPublishedSubtitle(story) {
  return safeText(story?.publishedSubtitle || story?.publicationSubtitle || story?.subtitle);
}

export function getPublicationInfo(story, langOrText = "en") {
  const lang =
    typeof langOrText === "string"
      ? langOrText
      : safeText(langOrText?.lang || langOrText?.language || "en");

  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const status = normalizePublicationStatus(story?.publicationStatus || story?.publishingStatus);
  const displayStatus = status;
  const isPublished = isPublishedStatus(status);
  const amazonHref = getPublicationUrl(story);
  const coverUrl = getPublicationCoverUrl(story);

  let publicationLabel = isFR ? "En développement" : "In Development";
  let developmentLabel = isFR ? "Storyline active" : "Active storyline";

  if (displayStatus === PUBLICATION_STATUSES.COMPLETED) {
    publicationLabel = isFR ? "Livre complété" : "Completed Book";
    developmentLabel = isFR ? "Aperçu sur NextScenes" : "Preview on NextScenes";
  } else if (displayStatus === PUBLICATION_STATUSES.PUBLISHED_AMAZON) {
    publicationLabel = isFR ? "Publié sur Amazon" : "Published on Amazon";
    developmentLabel = isFR ? "Aperçu sur NextScenes" : "Preview on NextScenes";
  } else if (displayStatus === PUBLICATION_STATUSES.PUBLISHED_EXTERNAL) {
    publicationLabel = isFR ? "Publié" : "Published";
    developmentLabel = isFR ? "Aperçu sur NextScenes" : "Preview on NextScenes";
  }

  return {
    status: displayStatus,
    isPublished,
    isPublicationLocked: isPublished,
    publicationLabel,
    developmentLabel,
    amazonHref,
    publicationUrl: amazonHref,
    coverUrl,
    publishedAt: safeText(story?.publishedAt),
    publishedAuthorName: getPublishedAuthorName(story),
    publishedTitle: getPublishedTitle(story),
    publishedSubtitle: getPublishedSubtitle(story),
    cta:
      status === PUBLICATION_STATUSES.PUBLISHED_AMAZON
        ? isFR
          ? "Voir sur Amazon"
          : "View on Amazon"
        : isFR
        ? "Voir la publication"
        : "View publication",
  };
}
